import React, { useState, useCallback } from 'react';
import { Grid, Row, Col, Panel, Button } from 'react-bootstrap';
import { exportAsExcel } from '../../Services/exportAsExcel';
import { fetchApi } from '../../Network/NetworkService';
import { typeStates } from '../../Constants';

// Theme options come from the canonical typeStates list so the values match the
// request `type` field exactly (e.g. "generaliste", not "medecine-generale").
// "" = all themes; the per-theme breakdown below still reports every match.
const THEME_OPTIONS = [{ value: '', label: 'Tous les thèmes' }].concat(typeStates);

const prettyType = (type) => {
    if (!type) return '—';
    const known = typeStates.find((t) => t.value === type);
    if (known) return known.label;
    // Fallback for any legacy type not in the canonical list.
    return type.replace(/-/g, ' ');
};

// Show a ~240 char window of the answers centred on the matched term.
const buildExtrait = (text, term) => {
    if (!text) return '';
    const clean = text.replace(/\s+/g, ' ').trim();
    const idx = clean.toLowerCase().indexOf(term.toLowerCase());
    if (idx === -1) return clean.length > 240 ? clean.slice(0, 240) + '…' : clean;
    const start = Math.max(0, idx - 80);
    const end = Math.min(clean.length, idx + term.length + 160);
    return (start > 0 ? '…' : '') + clean.slice(start, end) + (end < clean.length ? '…' : '');
};

const RechercheSymptomes = () => {
    const [term, setTerm] = useState('');
    const [filters, setFilters] = useState({
        start: '2023-01-01',
        end: new Date().toISOString().split('T')[0],
        type: ''
    });
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const search = useCallback(async () => {
        const q = term.trim();
        if (!q) {
            setError('Veuillez saisir un terme à rechercher.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const params = [
                `q=${encodeURIComponent(q)}`,
                `start=${filters.start}`,
                `end=${filters.end}`
            ];
            if (filters.type) params.push(`typeState=${encodeURIComponent(filters.type)}`);
            const response = await fetchApi(`/requests/analytics/symptom-search?${params.join('&')}`, 'GET');
            if (response.success) {
                setResults(response);
            } else {
                setError('La recherche a échoué.');
            }
        } catch (err) {
            setError(`Erreur lors de la recherche : ${err.message || err}`);
        } finally {
            setLoading(false);
        }
    }, [term, filters]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') search();
    };

    const handleExport = () => {
        if (!results || !results.list.length) return;
        const data = results.list.map((r) => ({
            'Patient': r.patientName || '—',
            'Email': r.patientEmail || '—',
            'Date': r.createdAt ? new Date(r.createdAt).toLocaleDateString('fr-FR') : '—',
            'Médecin': r.doctorName || '—',
            'Thème': prettyType(r.type),
            'État': r.state || '—',
            'Extrait': buildExtrait(r.extrait, results.term)
        }));
        exportAsExcel(data, `recherche_${results.term.replace(/\s+/g, '_')}`);
    };

    return (
        <Grid fluid>
            <Row>
                <Col xs={12}>
                    <h2>Recherche symptômes</h2>
                    <p className="text-muted">
                        Recherche un terme (ex : « perte de poids ») dans les réponses au questionnaire
                        de toutes les consultations, et compte combien en sont concernées.
                    </p>
                </Col>
            </Row>

            {error && (
                <Row className="mb-3">
                    <Col xs={12}>
                        <div className="alert alert-danger alert-dismissible" role="alert">
                            <button type="button" className="close" onClick={() => setError(null)}>
                                <span>&times;</span>
                            </button>
                            {error}
                        </div>
                    </Col>
                </Row>
            )}

            <Row className="mb-4">
                <Col md={4}>
                    <div className="form-group">
                        <label>Terme recherché</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="perte de poids"
                            value={term}
                            onChange={(e) => setTerm(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                    </div>
                </Col>
                <Col md={2}>
                    <div className="form-group">
                        <label>Date début</label>
                        <input
                            type="date"
                            className="form-control"
                            value={filters.start}
                            onChange={(e) => setFilters({ ...filters, start: e.target.value })}
                        />
                    </div>
                </Col>
                <Col md={2}>
                    <div className="form-group">
                        <label>Date fin</label>
                        <input
                            type="date"
                            className="form-control"
                            value={filters.end}
                            onChange={(e) => setFilters({ ...filters, end: e.target.value })}
                        />
                    </div>
                </Col>
                <Col md={2}>
                    <div className="form-group">
                        <label>Thème</label>
                        <select
                            className="form-control"
                            value={filters.type}
                            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                        >
                            {THEME_OPTIONS.map((t) => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                    </div>
                </Col>
                <Col md={2}>
                    <div className="form-group" style={{ paddingTop: '25px' }}>
                        <Button bsStyle="primary" onClick={search} disabled={loading} block>
                            {loading ? 'Recherche…' : 'Rechercher'}
                        </Button>
                    </div>
                </Col>
            </Row>

            {results && (
                <>
                    <Row className="mb-4">
                        <Col md={4}>
                            <Panel>
                                <Panel.Body style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '42px', fontWeight: 'bold', color: '#337ab7' }}>
                                        {results.total}
                                    </div>
                                    <div className="text-muted">
                                        consultation{results.total > 1 ? 's' : ''} contenant
                                        « {results.term} »
                                    </div>
                                    {results.truncated && (
                                        <div className="text-warning" style={{ marginTop: '8px', fontSize: '12px' }}>
                                            Liste limitée aux 1000 plus récentes (le total ci-dessus reste exact).
                                        </div>
                                    )}
                                </Panel.Body>
                            </Panel>
                        </Col>
                        <Col md={8}>
                            <Panel>
                                <Panel.Heading>
                                    <h4>Répartition par thème</h4>
                                </Panel.Heading>
                                <Panel.Body style={{ maxHeight: '220px', overflowY: 'auto' }}>
                                    {results.byTheme.length === 0 ? (
                                        <p className="text-muted">Aucun résultat.</p>
                                    ) : (
                                        <table className="table table-striped table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>Thème</th>
                                                    <th style={{ width: '120px' }}>Nombre</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {results.byTheme.map((t) => (
                                                    <tr key={t.type || 'none'}>
                                                        <td>{prettyType(t.type)}</td>
                                                        <td>{t.count}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </Panel.Body>
                            </Panel>
                        </Col>
                    </Row>

                    <Row className="mb-4">
                        <Col xs={12}>
                            <Panel>
                                <Panel.Heading style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h4 style={{ margin: 0 }}>Consultations ({results.list.length})</h4>
                                    <Button bsStyle="success" bsSize="small" onClick={handleExport} disabled={!results.list.length}>
                                        Exporter Excel
                                    </Button>
                                </Panel.Heading>
                                <Panel.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                    <table className="table table-striped table-bordered table-hover">
                                        <thead style={{ position: 'sticky', top: 0, background: 'white' }}>
                                            <tr>
                                                <th>Patient</th>
                                                <th>Email</th>
                                                <th>Date</th>
                                                <th>Médecin</th>
                                                <th>Thème</th>
                                                <th>Extrait</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {results.list.map((r) => (
                                                <tr key={r._id}>
                                                    <td>{r.patientName || '—'}</td>
                                                    <td>{r.patientEmail || '—'}</td>
                                                    <td>{r.createdAt ? new Date(r.createdAt).toLocaleDateString('fr-FR') : '—'}</td>
                                                    <td>{r.doctorName || '—'}</td>
                                                    <td>{prettyType(r.type)}</td>
                                                    <td style={{ fontSize: '12px', color: '#555' }}>
                                                        {buildExtrait(r.extrait, results.term)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Panel.Body>
                            </Panel>
                        </Col>
                    </Row>
                </>
            )}
        </Grid>
    );
};

export default RechercheSymptomes;
